import { Controller, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Box, Stack, Typography } from '@mui/material';

import { useTranslation } from '@mopla/i18';

import { MplInputV2 } from '../../atoms/MplInputV2';

import {
	DateOfBirthFormInputContainer,
	DateOfBirthFormInputErrorMessage,
} from './styled';

type FieldConfig = {
	name: 'day' | 'month' | 'year';
	label: string;
	maxLength: number;
};

type DateOfBirthFormInputProps = {
	dataTestIdPrefix: string;
	onValidateDate: () => void;
};

export const DateOfBirthFormInput = ({
	dataTestIdPrefix,
	onValidateDate,
}: DateOfBirthFormInputProps) => {
	const { t } = useTranslation(['validation', 'personalDetails']);
	const { formState } = useFormContext();

	const fields: FieldConfig[] = [
		{ name: 'day', label: `${t('personalDetails:label.day')} *`, maxLength: 2 },
		{
			name: 'month',
			label: `${t('personalDetails:label.month')} *`,
			maxLength: 2,
		},
		{
			name: 'year',
			label: `${t('personalDetails:label.year')} *`,
			maxLength: 4,
		},
	];

	return (
		<DateOfBirthFormInputContainer>
			<Typography variant="b2" color="primary.main">
				{t('personalDetails:text.date_of_birth')}
			</Typography>
			<Stack direction="row" spacing={1}>
				{fields.map(({ name, label, maxLength }) => (
					<Controller
						key={name}
						name={name}
						render={({ field }) => (
							<Box sx={{ width: '33%' }}>
								<MplInputV2
									{...field}
									ref={null}
									label={label}
									onBlur={() => {
										field.onBlur();
										onValidateDate();
									}}
									onChange={(e) => {
										const value = e.target.value.replace(/\D/g, '');
										field.onChange(value);
									}}
									error={Boolean(formState.errors?.dateOfBirth)}
									inputProps={{
										maxLength,
										inputMode: 'numeric',
										'data-testid': `${dataTestIdPrefix}-${name}-textField`,
									}}
									clearable
								/>
							</Box>
						)}
					/>
				))}
			</Stack>
			<ErrorMessage
				name="dateOfBirth"
				render={({ message }) => (
					<DateOfBirthFormInputErrorMessage
						error
						data-testid={`${dataTestIdPrefix}-date-of-birth-error`}
					>
						{t(`${message}`)}
					</DateOfBirthFormInputErrorMessage>
				)}
			/>
		</DateOfBirthFormInputContainer>
	);
};
